import { useAuth } from '@/hooks/useAuth';
import { useOfflineReportSubmissionQueue } from '@/screens/ReportCreation/hooks/offlineReportSubmissionQueue/useOfflineReportSubmissionQueue';
import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';

const OFFLINE_QUEUE_RETRY_INTERVAL_MS = 15000;

export function OfflineReportSubmissionQueueProcessor(): null {
  const { isAuthenticated, status } = useAuth();
  const { processReportSubmissionQueue } = useOfflineReportSubmissionQueue();
  // Keep the latest process function without recreating listeners.
  const processQueueRef = useRef(processReportSubmissionQueue);

  useEffect(() => {
    // Update ref when hook returns a new callback.
    processQueueRef.current = processReportSubmissionQueue;
  }, [processReportSubmissionQueue]);

  useEffect(() => {
    // Only process queue for authenticated users.
    if (!isAuthenticated || status !== 'authenticated') {
      return;
    }

    // Run once immediately when processor starts.
    void processQueueRef.current();

    // Retry queue periodically while app is open.
    const interval = setInterval(() => {
      void processQueueRef.current();
    }, OFFLINE_QUEUE_RETRY_INTERVAL_MS);

    // Retry as soon as the app returns to foreground.
    const appStateSubscription = AppState.addEventListener(
      'change',
      (nextState): void => {
        if (nextState === 'active') {
          void processQueueRef.current();
        }
      },
    );

    return (): void => {
      // Clean up interval and app-state listener.
      clearInterval(interval);
      appStateSubscription.remove();
    };
  }, [isAuthenticated, status]);

  return null;
}
